/**
 * TimeEntryForm — record or edit a single time entry against the native
 * ledger (POST/PATCH /time-entries).
 *
 * Two modes:
 *   - create (no `editingEntry`): matter, date, duration, narrative, rate
 *     type, the unbillable toggle and the costs tag are all editable.
 *     `matterRef` pre-fills and hides the matter picker when embedded on a
 *     matter's own page.
 *   - edit (`editingEntry` passed, draft-only): matter/date/rate type are
 *     shown read-only — hub.time_entries.update_time_entry only accepts
 *     narrative/duration_hours/chargeable/cost_tag, so those are the only
 *     fields this form lets you change once an entry exists.
 *
 * Costs tag (30 Sep 2026, Vanessa: "for dispute matters can we have an
 * optional tag to assist with costs schedules") — the standard Precedent H
 * / N260 categories. Always optional and never gated to a matter type:
 * most matters aren't disputes and simply never set one.
 *
 * Units are 6 minutes (0.1h) firm-wide — duration always rounds UP to the
 * next 0.1 on blur, matching create_time_entry's InvalidDurationError
 * (multiples of 0.1 only) instead of letting a submit fail after the fact.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import SmartClientMatterSearch from "./SmartClientMatterSearch";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Sparkles, Loader2 } from "lucide-react";
import { STAFF } from "../config/staffConfig";
import { getUserRole, ROLES } from "../config/roleConfig";
import { currentUserEmail } from "../utils/currentUserEmail";

// BM-H3415 (1 Oct 2026), Vanessa: "allow us to post for each other in
// coco team" -- same synchronous session-identity read useCurrentUser()
// uses internally, without the extra GET /staff/me round-trip this form
// doesn't otherwise need. The backend (hub.routes_time_entries.
// _require_create_permission) is authoritative either way: this only
// decides what the picker OFFERS, never what it's allowed to submit.

const RATE_TYPES = [
  { value: "standard", label: "Standard" },
  { value: "complex_expedite", label: "Expedited" },
];
const COST_TAGS = [
  { value: "attendance_on_client", label: "Attendance on client" },
  { value: "attendance_on_other_side", label: "Attendance on other side" },
  { value: "attendance_on_court", label: "Attendance on court" },
  { value: "work_on_documents", label: "Work on documents" },
  { value: "disclosure", label: "Disclosure" },
  { value: "advocacy", label: "Advocacy" },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function roundUpToUnit(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round((Math.ceil(n * 10 - 1e-9) / 10) * 10) / 10;
}

export default function TimeEntryForm({ matterRef = "", editingEntry = null, onSaved, onCancel }) {
  const isEdit = !!editingEntry;

  const [matterReference, setMatterReference] = useState(matterRef || editingEntry?.matter_ref || "");
  const [matterLabel, setMatterLabel] = useState(editingEntry?.matter_ref || "");
  const [entryDate, setEntryDate] = useState(editingEntry?.entry_date || todayIso());
  const [duration, setDuration] = useState(
    editingEntry ? Number(editingEntry.duration_hours) : 0.1,
  );
  const [narrative, setNarrative] = useState(editingEntry?.narrative || "");
  const [rateType, setRateType] = useState(editingEntry?.rate_type || "standard");
  const [costTag, setCostTag] = useState(editingEntry?.cost_tag || "");
  const myEmail = currentUserEmail();
  const [feeEarnerEmail, setFeeEarnerEmail] = useState(myEmail);
  const isDirector = getUserRole(myEmail) === ROLES.DIRECTOR;
  const myDashboardRole = STAFF.find((s) => s.email === myEmail)?.dashboard_role;
  const feeEarnerOptions = STAFF.filter(
    (s) => s.is_active && s.email && (isDirector || s.email === myEmail || (myDashboardRole && s.dashboard_role === myDashboardRole)),
  );
  const [unbillable, setUnbillable] = useState(editingEntry ? !editingEntry.chargeable : false);
  const [saving, setSaving] = useState(false);
  // BM-H3459: see TimeClockList. Remounts the search box once the saved
  // matter is cleared, so it cannot go on showing a matter it no longer holds.
  const [searchKey, setSearchKey] = useState(0);
  const [suggesting, setSuggesting] = useState(false);

  const handleMatterSelect = (result) => {
    setMatterReference(result?.matter_reference || "");
    setMatterLabel(result?.label || result?.matter_reference || "");
  };

  const handleDurationBlur = () => {
    setDuration((d) => roundUpToUnit(d));
  };

  // 30 Sep 2026 (Vanessa): "let fee earners enter hours and minutes as
  // they find units confusing" -- the raw decimal is still the single
  // source of truth (`duration`, submitted and rounded exactly as
  // before); these two fields are just a friendlier way to set it.
  // Derived from `duration` on every render rather than kept as separate
  // state, so a preset-button click and an edit-mode pre-fill both "just
  // work" without a second sync path to keep correct.
  const durationHoursPart = Math.floor(duration);
  const durationMinutesPart = Math.round((duration - durationHoursPart) * 60);

  const handleHoursInputChange = (e) => {
    const h = Math.max(0, parseInt(e.target.value, 10) || 0);
    setDuration(h + durationMinutesPart / 60);
  };

  const handleMinutesInputChange = (e) => {
    // Roll over rather than clamp at 59: typing 75 minutes means 1h15m,
    // not a silently truncated 59m -- clamping would quietly under-bill.
    const rawMinutes = Math.max(0, parseInt(e.target.value, 10) || 0);
    const rolloverHours = Math.floor(rawMinutes / 60);
    const minutes = rawMinutes % 60;
    setDuration(durationHoursPart + rolloverHours + minutes / 60);
  };

  const handleSuggestNarrative = async () => {
    if (!narrative.trim()) {
      toast.error("Type a quick note about what you did first, then suggest.");
      return;
    }
    setSuggesting(true);
    try {
      const resp = await axios.post(
        `${API}/time-entries/ai-narrative`,
        { content: narrative, subject_hint: matterLabel },
        { withCredentials: true },
      );
      if (resp.data?.narrative) setNarrative(resp.data.narrative);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't get an AI suggestion — your note is unchanged."));
    }
    setSuggesting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !matterReference) {
      toast.error("Pick a matter first: choose it from the search results, typing the name is not enough.");
      return;
    }
    const roundedDuration = roundUpToUnit(duration);
    if (roundedDuration <= 0) {
      toast.error("Duration must be more than 0.");
      return;
    }
    if (!narrative.trim()) {
      toast.error("A narrative is required.");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        const resp = await axios.patch(
          `${API}/time-entries/${editingEntry.id}`,
          {
            narrative: narrative.trim(), duration_hours: roundedDuration, chargeable: !unbillable,
            cost_tag: costTag,
          },
          { withCredentials: true },
        );
        toast.success("Time entry updated.");
        if (onSaved) onSaved(resp.data);
      } else {
        const resp = await axios.post(
          `${API}/time-entries`,
          {
            matter_ref: matterReference,
            fee_earner_email: feeEarnerEmail,
            entry_date: entryDate,
            duration_hours: roundedDuration,
            narrative: narrative.trim(),
            chargeable: !unbillable,
            rate_type: rateType,
            cost_tag: costTag || null,
            source: "manual",
          },
          { withCredentials: true },
        );
        toast.success("Time recorded.");
        if (!matterRef) { setMatterReference(""); setMatterLabel(""); setSearchKey((k) => k + 1); }
        setDuration(0.1);
        setNarrative("");
        setUnbillable(false);
        setCostTag("");
        setFeeEarnerEmail(myEmail);
        if (onSaved) onSaved(resp.data);
      }
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't save that time entry."));
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="time-entry-form">
      {!matterRef && !isEdit && (
        <SmartClientMatterSearch
          key={searchKey}
          label="Matter"
          placeholder="Search matters..."
          mattersOnly
          onSelect={handleMatterSelect}
          initialValue={matterLabel}
        />
      )}
      {(matterRef || isEdit) && (
        <div>
          <Label className="text-sm font-medium text-teal">Matter</Label>
          <div className="text-sm text-white mt-1" data-testid="time-entry-matter-ref">
            {matterReference}
          </div>
        </div>
      )}

      {!isEdit && feeEarnerOptions.length > 1 && (
        <div>
          <Label className="text-sm font-medium text-teal">For</Label>
          <select
            value={feeEarnerEmail}
            onChange={(e) => setFeeEarnerEmail(e.target.value)}
            className="mt-1 w-full rounded-md border border-navy-border bg-navy-card px-2.5 py-1.5 text-sm text-white"
            data-testid="time-entry-fee-earner"
          >
            {feeEarnerOptions.map((s) => (
              <option key={s.email} value={s.email}>
                {s.email === myEmail ? `${s.full_name} (you)` : s.full_name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-sm font-medium text-teal">Date</Label>
          <Input
            type="date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            disabled={isEdit}
            className="mt-1"
            data-testid="time-entry-date"
          />
        </div>
        <div>
          <Label className="text-sm font-medium text-teal">Rate</Label>
          <div className="flex gap-1 mt-1" data-testid="time-entry-rate-type">
            {RATE_TYPES.map((rt) => (
              <button
                key={rt.value}
                type="button"
                disabled={isEdit}
                onClick={() => setRateType(rt.value)}
                className={`text-xs px-2.5 py-1.5 rounded-md border ${
                  rateType === rt.value
                    ? "bg-teal-dim/30 border-teal text-teal"
                    : "border-navy-border text-slate-400"
                } ${isEdit ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid={`time-entry-rate-${rt.value}`}
              >
                {rt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <Label className="text-sm font-medium text-teal">Costs tag (optional, for dispute matters)</Label>
        <div className="flex flex-wrap gap-1.5 mt-1" data-testid="time-entry-cost-tag">
          {COST_TAGS.map((ct) => (
            <button
              key={ct.value}
              type="button"
              onClick={() => setCostTag((c) => (c === ct.value ? "" : ct.value))}
              className={`text-xs px-2.5 py-1.5 rounded-md border ${
                costTag === ct.value
                  ? "bg-teal-dim/30 border-teal text-teal"
                  : "border-navy-border text-slate-400"
              }`}
              data-testid={`time-entry-cost-tag-${ct.value}`}
            >
              {ct.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-sm font-medium text-teal">Duration</Label>
        <div className="flex flex-wrap items-end gap-3 mt-1">
          <div>
            <span className="block text-xs text-slate-400 mb-1">Hours</span>
            <Input
              type="number"
              min="0"
              value={durationHoursPart}
              onChange={handleHoursInputChange}
              onBlur={handleDurationBlur}
              className="w-20"
              data-testid="time-entry-duration-hours-input"
            />
          </div>
          <div>
            <span className="block text-xs text-slate-400 mb-1">Minutes</span>
            <Input
              type="number"
              min="0"
              value={durationMinutesPart}
              onChange={handleMinutesInputChange}
              onBlur={handleDurationBlur}
              className="w-20"
              data-testid="time-entry-duration-minutes-input"
            />
          </div>
          <span className="text-[11px] text-slate-500 pb-2" data-testid="time-entry-duration-rounded-hint">
            = {roundUpToUnit(duration).toFixed(1)}h billed (6-minute units)
          </span>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-teal">Narrative</Label>
          <button
            type="button"
            onClick={handleSuggestNarrative}
            disabled={suggesting}
            className="text-xs text-teal flex items-center gap-1 hover:underline disabled:opacity-50"
            data-testid="time-entry-suggest-btn"
          >
            {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            AI Suggest
          </button>
        </div>
        <Textarea
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          placeholder="What did you do?"
          className="mt-1"
          rows={3}
          data-testid="time-entry-narrative"
        />
      </div>

      <div className="flex items-center gap-2">
        <Switch
          checked={unbillable}
          onCheckedChange={setUnbillable}
          data-testid="time-entry-unbillable-toggle"
        />
        <Label className="text-sm text-slate-300">Unbillable</Label>
      </div>

      <div className="flex gap-2 pt-1">
        <Button type="submit" disabled={saving} data-testid="time-entry-save-btn">
          {saving ? "Saving..." : isEdit ? "Save changes" : "Log time"}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} data-testid="time-entry-cancel-btn">
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
